// src/redux/formCombinedSlices.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getPagesByWebUri,
  getQuestionsByPageId,
  getFormStatusByWebUri,
  postFormAnswersByWebUri,
  getFormConfigByFormtype,
} from "../../services/form-services/form.services.js";

/* --- formSlice --- */
export const fetchPages = createAsyncThunk(
  "form/fetchPages",
  async ({ web_uri }) => {
    try {
      const response = await getPagesByWebUri(web_uri);
      return response.data;
    } catch (error) {
      console.error("Error in fetchPages:", error);
      // Si hay un error, devolvemos un array vacío en lugar de fallar
      return [];
    }
  }
);

export const fetchQuestions = createAsyncThunk(
  "form/fetchQuestions",
  async (pageId) => {
    const response = await getQuestionsByPageId(pageId);
    return response.data;
  }
);

const formSlice = createSlice({
  name: "form",
  initialState: {
    pages: [],
    questions: [],
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPages.fulfilled, (state, action) => {
        state.pages = action.payload;
      })
      .addCase(fetchQuestions.fulfilled, (state, action) => {
        state.questions = action.payload;
      });
  },
});

/* --- formStateSlice --- */
export const fetchFormState = createAsyncThunk(
  "formState/fetchFormState",
  async ({ web_uri }, { rejectWithValue }) => {
    try {
      const data = await getFormStatusByWebUri(web_uri);
      return data;
    } catch (error) {
      console.error("fetchFormState error:", error);

      // Si el error es específicamente que el formulario no se encontró,
      // devolvemos un objeto con form_found: false
      if (
        error.message.includes("Form not found") ||
        error.message.includes("404")
      ) {
        return {
          form_found: false,
          form_already_submitted: false,
          ya_fue_completado: false,
          completed: false,
          error_message: error.message,
        };
      }

      // Para otros errores, los rechazamos
      return rejectWithValue({
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
      });
    }
  }
);

const formStateSlice = createSlice({
  name: "formState",
  initialState: {
    form_found: null,
    form_already_submitted: null,
    ya_fue_completado: null,
    completed: null,
    form_id: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFormState.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFormState.fulfilled, (state, action) => {
        state.loading = false;
        state.form_found = action.payload.form_found;
        state.form_already_submitted =
          action.payload.form_already_submitted ?? false;
        state.ya_fue_completado = action.payload.ya_fue_completado ?? false;
        state.completed = action.payload.completed ?? false;
        state.form_id = action.payload.form_id ?? null;
      })
      .addCase(fetchFormState.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
        state.form_found = false;
        state.form_already_submitted = false;
        state.ya_fue_completado = false;
        state.completed = false;
        state.form_id = null;
      });
  },
});

// Función de validación de respuestas
const validateAnswers = (answers, questions) => {
  if (!answers || typeof answers !== "object") {
    return { isValid: false, errors: ["Las respuestas no son válidas"] };
  }

  if (!questions || !Array.isArray(questions)) {
    return { isValid: false, errors: ["Las preguntas no son válidas"] };
  }

  const errors = [];
  const answerKeys = Object.keys(answers);

  // Verificar que todas las preguntas tengan respuesta
  for (const question of questions) {
    const questionId = question.id;
    const answer = answers[questionId];

    if (answer === undefined || answer === null || answer === "") {
      errors.push(`Pregunta ${questionId} no tiene respuesta`);
    } else if (
      question.type === "multiple_check" &&
      (!Array.isArray(answer) || answer.length === 0)
    ) {
      errors.push(`Pregunta ${questionId} (checkbox) no tiene selecciones`);
    }
  }

  // Verificar que no haya respuestas extra
  for (const answerKey of answerKeys) {
    const questionExists = questions.some((q) => q.id === answerKey);
    if (!questionExists) {
      // Respuesta extra encontrada, pero no es un error crítico
    }
  }

  const isValid = errors.length === 0;

  return { isValid, errors };
};

/* --- formAnswersSlice --- */
export const submitAnswers = createAsyncThunk(
  "formAnswers/submitAnswers",
  async (
    { web_uri, answers, behavioral_answers, form_id, questions },
    { getState, rejectWithValue }
  ) => {
    // Si no se pasó form_id, intentamos obtenerlo del estado
    let finalFormId = form_id;
    if (!finalFormId) {
      const state = getState();
      finalFormId = state.formState.form_id;
    }

    // Validación de respuestas antes del envío
    const validation = validateAnswers(answers, questions);
    if (!validation.isValid) {
      return rejectWithValue({
        message: "El formulario contiene errores de validación",
        errors: validation.errors,
      });
    }

    // Verificar que no estemos enviando un formulario vacío
    const answerKeys = Object.keys(answers);
    if (answerKeys.length === 0) {
      return rejectWithValue({
        message: "No se pueden enviar respuestas vacías",
        errors: ["El formulario está vacío"],
      });
    }

    const result = await postFormAnswersByWebUri({
      web_uri,
      answers,
      behavioral_answers,
      form_id: finalFormId,
    });

    return result;
  }
);

const formAnswersSlice = createSlice({
  name: "formAnswers",
  initialState: {
    answers: {},
    status: "idle",
    error: null,
    validationErrors: [],
    lastSubmission: null,
  },
  reducers: {
    setAnswer: (state, action) => {
      const { questionId, value } = action.payload;

      // Validación básica del valor
      if (value === undefined || value === null) {
        return;
      }

      state.answers[questionId] = value;
    },
    resetAnswers: (state) => {
      state.answers = {};
      state.validationErrors = [];
      state.lastSubmission = null;
    },
    clearValidationErrors: (state) => {
      state.validationErrors = [];
    },
    setValidationErrors: (state, action) => {
      state.validationErrors = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitAnswers.pending, (state) => {
        state.status = "loading";
        state.error = null;
        state.validationErrors = [];
      })
      .addCase(submitAnswers.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.lastSubmission = {
          timestamp: new Date().toISOString(),
          data: action.payload,
        };
      })
      .addCase(submitAnswers.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error?.message || "Error desconocido";
        state.validationErrors = action.payload?.errors || [];
      });
  },
});

/* --- formConfigSlice --- */
export const fetchFormConfig = createAsyncThunk(
  "formConfig/fetchFormConfig",
  async (formType) => {
    const data = await getFormConfigByFormtype(formType);
    return data;
  }
);

const formConfigSlice = createSlice({
  name: "formConfig",
  initialState: {
    data: null,
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchFormConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFormConfig.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchFormConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

/* --- Exports --- */
export const formReducer = formSlice.reducer;
export const formStateReducer = formStateSlice.reducer;
export const formAnswersReducer = formAnswersSlice.reducer;
export const formConfigReducer = formConfigSlice.reducer;
export const {
  setAnswer,
  resetAnswers,
  clearValidationErrors,
  setValidationErrors,
} = formAnswersSlice.actions;
